// ============================================
// CATEGORY ROUTING PAGE - Configuration des destinations produits
// Avec prévisualisation en temps réel des écrans Bar et Cuisine
// ============================================

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
    ChefHat, Wine, RefreshCw, Save, RotateCcw, 
    Settings, Info, CheckCircle, AlertCircle, Eye,
    Monitor, Smartphone, Tablet, ArrowRight, Package,
    Utensils, GlassWater, Clock, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import MobilePageHeader from '../components/MobilePageHeader';

const CategoryRoutingPage = () => {
    const { api, user } = useAuth();
    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [pendingChanges, setPendingChanges] = useState({});
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [showPreview, setShowPreview] = useState(true);
    
    const isAdmin = ['ADMIN', 'DIRECTEUR'].includes(user?.role);

    // Charger les catégories et produits
    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [catResponse, prodResponse] = await Promise.all([
                api().get('/bar/category-destinations'),
                api().get('/products?limit=500')
            ]);
            setCategories(catResponse.data || []);
            setProducts(prodResponse.data || []);
            setPendingChanges({});
        } catch (error) {
            console.error('Erreur chargement:', error);
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    }, [api]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Obtenir la destination actuelle (avec changements en attente)
    const getCurrentDestination = useCallback((category) => {
        if (pendingChanges[category.category_id] !== undefined) {
            return pendingChanges[category.category_id];
        }
        return category.destination;
    }, [pendingChanges]);

    // Changer la destination d'une catégorie (local)
    const handleDestinationChange = (categoryId, newDestination) => {
        setPendingChanges(prev => ({
            ...prev,
            [categoryId]: newDestination
        }));
    };

    // Calculer les produits par destination en temps réel
    const previewData = useMemo(() => {
        const barProducts = [];
        const kitchenProducts = [];
        
        products.forEach(product => {
            const category = categories.find(c => 
                c.category_id === product.category_id || 
                c.category_name?.toLowerCase() === product.category?.toLowerCase()
            );
            
            if (category) {
                const dest = getCurrentDestination(category);
                if (dest === 'bar') {
                    barProducts.push({ ...product, categoryName: category.category_name });
                } else {
                    kitchenProducts.push({ ...product, categoryName: category.category_name });
                }
            } else {
                // Par défaut en cuisine si pas de catégorie trouvée
                kitchenProducts.push({ ...product, categoryName: product.category || 'Non catégorisé' });
            }
        });
        
        return { barProducts, kitchenProducts };
    }, [products, categories, getCurrentDestination]);

    // Grouper les produits par catégorie pour la prévisualisation
    const groupByCategory = (productList) => {
        const grouped = {};
        productList.forEach(p => {
            const cat = p.categoryName || 'Autres';
            if (!grouped[cat]) grouped[cat] = [];
            grouped[cat].push(p);
        });
        return grouped;
    };

    // Sauvegarder tous les changements
    const saveAllChanges = async () => {
        if (Object.keys(pendingChanges).length === 0) {
            toast.info('Aucun changement à sauvegarder');
            return;
        }

        setSaving(true);
        try {
            const promises = Object.entries(pendingChanges).map(([categoryId, destination]) =>
                api().put('/bar/category-destinations', {
                    category_id: categoryId,
                    destination: destination
                })
            );
            
            await Promise.all(promises);
            toast.success(`${Object.keys(pendingChanges).length} catégorie(s) mise(s) à jour`);
            setPendingChanges({});
            fetchData();
        } catch (error) {
            console.error('Erreur sauvegarde:', error);
            toast.error('Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    // Réinitialiser une catégorie
    const resetToDefault = async (categoryId) => {
        try {
            await api().delete(`/bar/category-destinations/${categoryId}`);
            toast.success('Catégorie réinitialisée');
            fetchData();
        } catch (error) {
            toast.error('Erreur lors de la réinitialisation');
        }
    };

    const cancelChanges = () => {
        setPendingChanges({});
        toast.info('Changements annulés');
    };

    // Stats
    const barCategories = categories.filter(c => getCurrentDestination(c) === 'bar');
    const kitchenCategories = categories.filter(c => getCurrentDestination(c) === 'cuisine');
    const hasPendingChanges = Object.keys(pendingChanges).length > 0;

    // Composant de prévisualisation d'écran
    const ScreenPreview = ({ type, productList, icon: Icon, color, bgColor }) => {
        const grouped = groupByCategory(productList);
        const categoryCount = Object.keys(grouped).length;
        
        return (
            <div className={`rounded-2xl border-2 ${bgColor} overflow-hidden h-full`}>
                {/* Header de l'écran */}
                <div className={`${color} text-white p-3 sm:p-4`}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
                            <span className="font-bold text-base sm:text-lg">{type === 'bar' ? 'ÉCRAN BAR' : 'ÉCRAN CUISINE'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Badge className="bg-white/20 text-white text-xs">
                                {productList.length} produits
                            </Badge>
                            <Badge className="bg-white/20 text-white text-xs">
                                {categoryCount} catégories
                            </Badge>
                        </div>
                    </div>
                </div>
                
                {/* Corps de l'écran - Simulation */}
                <div className="p-2 sm:p-3">
                    <div className="bg-white dark:bg-gray-900 rounded-xl p-2 sm:p-3 min-h-[200px] sm:min-h-[300px] max-h-[300px] sm:max-h-[400px] overflow-y-auto">
                        {categoryCount === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full py-8 text-gray-400">
                                <Icon className="w-12 h-12 mb-2 opacity-30" />
                                <p className="text-sm">Aucun produit assigné</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {Object.entries(grouped).map(([catName, catProducts]) => (
                                    <div key={catName} className="space-y-1">
                                        <div className={`text-xs font-semibold ${type === 'bar' ? 'text-purple-600' : 'text-blue-600'} uppercase tracking-wide flex items-center gap-1`}>
                                            <Package className="w-3 h-3" />
                                            {catName} ({catProducts.length})
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                                            {catProducts.slice(0, 4).map((product, idx) => (
                                                <div 
                                                    key={product.id || idx}
                                                    className={`text-xs p-2 rounded-lg border ${
                                                        type === 'bar' 
                                                            ? 'bg-purple-50 border-purple-200 dark:bg-purple-950/30' 
                                                            : 'bg-blue-50 border-blue-200 dark:bg-blue-950/30'
                                                    }`}
                                                >
                                                    <div className="font-medium truncate">{product.name}</div>
                                                    <div className="text-gray-500 text-[10px]">
                                                        {product.price?.toLocaleString('fr-FR')} F
                                                    </div>
                                                </div>
                                            ))}
                                            {catProducts.length > 4 && (
                                                <div className={`text-xs p-2 rounded-lg border border-dashed ${
                                                    type === 'bar' ? 'border-purple-300 text-purple-500' : 'border-blue-300 text-blue-500'
                                                } flex items-center justify-center`}>
                                                    +{catProducts.length - 4} autres
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                
                {/* Footer avec stats */}
                <div className={`px-3 py-2 ${type === 'bar' ? 'bg-purple-100 dark:bg-purple-950/50' : 'bg-blue-100 dark:bg-blue-950/50'} text-xs`}>
                    <div className="flex items-center justify-between">
                        <span className={type === 'bar' ? 'text-purple-700' : 'text-blue-700'}>
                            Prévisualisation en temps réel
                        </span>
                        <Zap className={`w-3 h-3 ${type === 'bar' ? 'text-purple-500' : 'text-blue-500'}`} />
                    </div>
                </div>
            </div>
        );
    };

    // Composant carte catégorie
    const CategoryCard = ({ category }) => {
        const currentDest = getCurrentDestination(category);
        const isBar = currentDest === 'bar';
        const hasChange = pendingChanges[category.category_id] !== undefined;
        const categoryProducts = products.filter(p => 
            p.category_id === category.category_id || 
            p.category?.toLowerCase() === category.category_name?.toLowerCase()
        );
        
        return (
            <div 
                className={`
                    p-3 sm:p-4 rounded-xl border-2 transition-all duration-300 cursor-pointer
                    ${isBar 
                        ? 'bg-purple-50 border-purple-200 hover:border-purple-400 dark:bg-purple-950/30 dark:border-purple-800' 
                        : 'bg-blue-50 border-blue-200 hover:border-blue-400 dark:bg-blue-950/30 dark:border-blue-800'
                    }
                    ${hasChange ? 'ring-2 ring-yellow-400 ring-offset-2' : ''}
                    ${selectedCategory === category.category_id ? 'ring-2 ring-offset-2 ' + (isBar ? 'ring-purple-500' : 'ring-blue-500') : ''}
                `}
                onClick={() => setSelectedCategory(category.category_id === selectedCategory ? null : category.category_id)}
            >
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                        <div className={`
                            w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0
                            ${isBar ? 'bg-purple-500' : 'bg-blue-500'}
                        `}>
                            {isBar ? (
                                <Wine className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                            ) : (
                                <ChefHat className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                            )}
                        </div>
                        <div className="min-w-0 flex-1">
                            <h3 className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base truncate">
                                {category.category_name}
                            </h3>
                            <div className="flex items-center gap-1 sm:gap-2 mt-0.5 flex-wrap">
                                <Badge 
                                    className={`text-[10px] sm:text-xs ${isBar ? "bg-purple-500" : "bg-blue-500 text-white"}`}
                                >
                                    {isBar ? 'Bar' : 'Cuisine'}
                                </Badge>
                                <Badge variant="outline" className="text-[10px] sm:text-xs">
                                    {categoryProducts.length} produits
                                </Badge>
                                {category.is_default && !hasChange && (
                                    <Badge variant="outline" className="text-[10px] sm:text-xs bg-gray-100">
                                        Auto
                                    </Badge>
                                )}
                                {hasChange && (
                                    <Badge className="bg-yellow-500 text-white text-[10px] sm:text-xs animate-pulse">
                                        Modifié
                                    </Badge>
                                )}
                            </div>
                        </div>
                    </div>
                    
                    <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                        {/* Toggle Bar/Cuisine */}
                        <div className="flex items-center gap-1 sm:gap-2" onClick={e => e.stopPropagation()}>
                            <ChefHat className={`w-3 h-3 sm:w-4 sm:h-4 ${!isBar ? 'text-blue-600' : 'text-gray-300'}`} />
                            <Switch
                                checked={isBar}
                                onCheckedChange={(checked) => 
                                    handleDestinationChange(category.category_id, checked ? 'bar' : 'cuisine')
                                }
                                disabled={!isAdmin}
                                className="data-[state=checked]:bg-purple-500 data-[state=unchecked]:bg-blue-500"
                            />
                            <Wine className={`w-3 h-3 sm:w-4 sm:h-4 ${isBar ? 'text-purple-600' : 'text-gray-300'}`} />
                        </div>
                        
                        {/* Bouton reset */}
                        {!category.is_default && isAdmin && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); resetToDefault(category.category_id); }}
                                className="text-gray-400 hover:text-gray-600 h-8 w-8 p-0"
                            >
                                <RotateCcw className="w-3 h-3 sm:w-4 sm:h-4" />
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
                <div className="text-center">
                    <RefreshCw className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-2" />
                    <p className="text-muted-foreground">Chargement...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
            <MobilePageHeader title="Routage Catégories" />
            
            <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <Settings className="w-5 h-5 sm:w-6 sm:h-6" />
                            Configuration des destinations
                        </h1>
                        <p className="text-xs sm:text-sm text-gray-500 mt-1">
                            Définissez où chaque catégorie de produit doit être envoyée
                        </p>
                    </div>
                    
                    <div className="flex items-center gap-2">
                        <Button
                            variant={showPreview ? "default" : "outline"}
                            size="sm"
                            onClick={() => setShowPreview(!showPreview)}
                            className="text-xs sm:text-sm"
                        >
                            <Eye className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                            <span className="hidden sm:inline">Prévisualisation</span>
                            <span className="sm:hidden">Aperçu</span>
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={fetchData}
                            disabled={loading}
                        >
                            <RefreshCw className={`w-3 h-3 sm:w-4 sm:h-4 ${loading ? 'animate-spin' : ''}`} />
                        </Button>
                    </div>
                </div>

                {/* Barre d'actions si changements en attente */}
                {hasPendingChanges && (
                    <Card className="mb-4 sm:mb-6 border-yellow-400 bg-yellow-50 dark:bg-yellow-950/30 animate-pulse-slow">
                        <CardContent className="p-3 sm:p-4">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                                <div className="flex items-center gap-2">
                                    <AlertCircle className="w-5 h-5 text-yellow-600" />
                                    <span className="font-medium text-yellow-800 dark:text-yellow-200 text-sm sm:text-base">
                                        {Object.keys(pendingChanges).length} modification(s) en attente
                                    </span>
                                </div>
                                <div className="flex items-center gap-2 w-full sm:w-auto">
                                    <Button
                                        variant="outline"
                                        onClick={cancelChanges}
                                        disabled={saving}
                                        size="sm"
                                        className="flex-1 sm:flex-none"
                                    >
                                        Annuler
                                    </Button>
                                    <Button
                                        onClick={saveAllChanges}
                                        disabled={saving}
                                        size="sm"
                                        className="bg-green-600 hover:bg-green-700 flex-1 sm:flex-none"
                                    >
                                        {saving ? (
                                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                        ) : (
                                            <Save className="w-4 h-4 mr-2" />
                                        )}
                                        Sauvegarder
                                    </Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* ================================== */}
                {/* PRÉVISUALISATION EN TEMPS RÉEL */}
                {/* ================================== */}
                {showPreview && (
                    <div className="mb-4 sm:mb-6">
                        <div className="flex items-center gap-2 mb-3">
                            <Eye className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                            <h2 className="font-semibold text-gray-800 dark:text-gray-200 text-sm sm:text-base">
                                Prévisualisation en temps réel
                            </h2>
                            <Badge variant="outline" className="text-[10px] sm:text-xs animate-pulse bg-green-50 text-green-700 border-green-300">
                                <Zap className="w-2 h-2 sm:w-3 sm:h-3 mr-1" />
                                Live
                            </Badge>
                        </div>
                        
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                            {/* Écran Bar */}
                            <ScreenPreview 
                                type="bar"
                                productList={previewData.barProducts}
                                icon={Wine}
                                color="bg-gradient-to-r from-purple-600 to-purple-500"
                                bgColor="border-purple-300 bg-purple-50/50 dark:bg-purple-950/20"
                            />
                            
                            {/* Écran Cuisine */}
                            <ScreenPreview 
                                type="kitchen"
                                productList={previewData.kitchenProducts}
                                icon={ChefHat}
                                color="bg-gradient-to-r from-blue-600 to-blue-500"
                                bgColor="border-blue-300 bg-blue-50/50 dark:bg-blue-950/20"
                            />
                        </div>
                        
                        {/* Stats résumées */}
                        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
                            <div className="bg-purple-100 dark:bg-purple-950/40 rounded-xl p-2 sm:p-3 text-center">
                                <div className="text-lg sm:text-2xl font-bold text-purple-600">{previewData.barProducts.length}</div>
                                <div className="text-[10px] sm:text-xs text-purple-500">Produits Bar</div>
                            </div>
                            <div className="bg-blue-100 dark:bg-blue-950/40 rounded-xl p-2 sm:p-3 text-center">
                                <div className="text-lg sm:text-2xl font-bold text-blue-600">{previewData.kitchenProducts.length}</div>
                                <div className="text-[10px] sm:text-xs text-blue-500">Produits Cuisine</div>
                            </div>
                            <div className="bg-purple-100 dark:bg-purple-950/40 rounded-xl p-2 sm:p-3 text-center">
                                <div className="text-lg sm:text-2xl font-bold text-purple-600">{barCategories.length}</div>
                                <div className="text-[10px] sm:text-xs text-purple-500">Catégories Bar</div>
                            </div>
                            <div className="bg-blue-100 dark:bg-blue-950/40 rounded-xl p-2 sm:p-3 text-center">
                                <div className="text-lg sm:text-2xl font-bold text-blue-600">{kitchenCategories.length}</div>
                                <div className="text-[10px] sm:text-xs text-blue-500">Catégories Cuisine</div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ================================== */}
                {/* LISTE DES CATÉGORIES */}
                {/* ================================== */}
                <Card>
                    <CardHeader className="pb-2 sm:pb-4">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                            <div>
                                <CardTitle className="text-base sm:text-lg">Catégories ({categories.length})</CardTitle>
                                <CardDescription className="text-xs sm:text-sm">
                                    Cliquez sur le toggle pour changer la destination
                                </CardDescription>
                            </div>
                            <Tabs defaultValue="all" className="w-full sm:w-auto">
                                <TabsList className="grid w-full grid-cols-3 h-8 sm:h-9">
                                    <TabsTrigger value="all" className="text-[10px] sm:text-xs px-2">
                                        Toutes ({categories.length})
                                    </TabsTrigger>
                                    <TabsTrigger value="bar" className="text-[10px] sm:text-xs px-2">
                                        <Wine className="w-3 h-3 mr-1 hidden sm:inline" />
                                        Bar ({barCategories.length})
                                    </TabsTrigger>
                                    <TabsTrigger value="kitchen" className="text-[10px] sm:text-xs px-2">
                                        <ChefHat className="w-3 h-3 mr-1 hidden sm:inline" />
                                        Cuisine ({kitchenCategories.length})
                                    </TabsTrigger>
                                </TabsList>
                            </Tabs>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                        <Tabs defaultValue="all">
                            <TabsContent value="all" className="mt-0">
                                <ScrollArea className="h-[300px] sm:h-[400px] pr-2 sm:pr-4">
                                    <div className="space-y-2 sm:space-y-3">
                                        {categories.map(category => (
                                            <CategoryCard key={category.category_id} category={category} />
                                        ))}
                                    </div>
                                </ScrollArea>
                            </TabsContent>
                            
                            <TabsContent value="bar" className="mt-0">
                                <ScrollArea className="h-[300px] sm:h-[400px] pr-2 sm:pr-4">
                                    <div className="space-y-2 sm:space-y-3">
                                        {barCategories.length === 0 ? (
                                            <div className="text-center py-8 text-gray-400">
                                                <Wine className="w-12 h-12 mx-auto mb-2 opacity-30" />
                                                <p className="text-sm">Aucune catégorie Bar</p>
                                            </div>
                                        ) : (
                                            barCategories.map(category => (
                                                <CategoryCard key={category.category_id} category={category} />
                                            ))
                                        )}
                                    </div>
                                </ScrollArea>
                            </TabsContent>
                            
                            <TabsContent value="kitchen" className="mt-0">
                                <ScrollArea className="h-[300px] sm:h-[400px] pr-2 sm:pr-4">
                                    <div className="space-y-2 sm:space-y-3">
                                        {kitchenCategories.length === 0 ? (
                                            <div className="text-center py-8 text-gray-400">
                                                <ChefHat className="w-12 h-12 mx-auto mb-2 opacity-30" />
                                                <p className="text-sm">Aucune catégorie Cuisine</p>
                                            </div>
                                        ) : (
                                            kitchenCategories.map(category => (
                                                <CategoryCard key={category.category_id} category={category} />
                                            ))
                                        )}
                                    </div>
                                </ScrollArea>
                            </TabsContent>
                        </Tabs>
                    </CardContent>
                </Card>

                {/* Légende */}
                <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-white dark:bg-gray-900 rounded-xl border">
                    <h3 className="font-semibold text-sm mb-2 flex items-center gap-2">
                        <Info className="w-4 h-4 text-blue-500" />
                        Comment ça marche ?
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                        <div className="flex items-start gap-2">
                            <Wine className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                            <span>Les produits <strong className="text-purple-600">Bar</strong> apparaissent sur l'écran des barmen</span>
                        </div>
                        <div className="flex items-start gap-2">
                            <ChefHat className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                            <span>Les produits <strong className="text-blue-600">Cuisine</strong> apparaissent sur l'écran des cuisiniers</span>
                        </div>
                        <div className="flex items-start gap-2">
                            <ArrowRight className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                            <span>Les formules sont automatiquement séparées selon leurs composants</span>
                        </div>
                        <div className="flex items-start gap-2">
                            <Zap className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                            <span>La prévisualisation se met à jour instantanément</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CategoryRoutingPage;
